import React, { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";
import { Upload, FileText, Trash2, Download, Loader2, Paperclip } from "lucide-react";
import { cn } from "@/lib/utils";

function humanSize(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function AttachmentsPanel({
  programId,
  stepNumber,
  canEdit = true,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  refreshTick = 0,
}) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/programs/${programId}/attachments`, { params: { step_number: stepNumber } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, [programId, stepNumber, refreshTick]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("step_number", String(stepNumber));
        await api.post(`/programs/${programId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) uploaded`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/attachments/${id}`);
      toast.success("File deleted");
      await load();
      if (selectedIds.includes(id) && onSelectionChange) onSelectionChange(selectedIds.filter((x) => x !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadFile = async (a) => {
    try {
      const token = localStorage.getItem("nf_token");
      const res = await fetch(`${API}/attachments/${a.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = a.original_filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const toggleSelect = (id) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(id)) onSelectionChange(selectedIds.filter((x) => x !== id));
    else onSelectionChange([...selectedIds, id]);
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <div>
          <input ref={inputRef} type="file" multiple onChange={(e) => upload(e.target.files)} className="hidden" data-testid={`att-input-${stepNumber}`} />
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`att-upload-${stepNumber}`}>
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Uploading…" : "Upload files (screenshots, Excel, PDF…)"}
          </Button>
          <div className="text-xs text-muted-foreground mt-1">Max 15 MB per file</div>
        </div>
      )}
      {items.length === 0 && <div className="text-sm text-muted-foreground py-3 flex items-center gap-2"><Paperclip className="w-4 h-4" /> No attachments yet.</div>}
      <div className="grid gap-2">
        {items.map((a) => (
          <div
            key={a.id}
            data-testid={`att-item-${a.id}`}
            className={cn(
              "flex items-center gap-3 p-3 border rounded-md bg-white transition-colors",
              selectable && selectedIds.includes(a.id) ? "border-primary/60 bg-primary/5" : "border-border"
            )}
          >
            {selectable && (
              <input
                type="checkbox"
                data-testid={`att-select-${a.id}`}
                checked={selectedIds.includes(a.id)}
                onChange={() => toggleSelect(a.id)}
                className="h-4 w-4"
              />
            )}
            <FileText className="w-4 h-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{a.original_filename}</div>
              <div className="text-[11px] text-muted-foreground">
                {humanSize(a.size)} · uploaded by {a.uploaded_by_name} · {new Date(a.created_at).toLocaleString()}
              </div>
            </div>
            <Button size="sm" variant="ghost" data-testid={`att-download-${a.id}`} onClick={() => downloadFile(a)} title="Download">
              <Download className="w-4 h-4" />
            </Button>
            {canEdit && (
              <Button size="sm" variant="ghost" data-testid={`att-delete-${a.id}`} onClick={() => remove(a.id)} title="Delete">
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
